import { Box, Chip, InputAdornment, MenuItem, Modal, OutlinedInput, Select, TextField } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Close, Search } from '@mui/icons-material';
import { useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from 'use-debounce';
import { useSelector } from 'react-redux';

import ModalStyle from '@/constants/ModalStyle';
import Pagination from '@/components/ui/Pagination/Pagination';
import Loader from '@/components/ui/Loader/Loader';

// import { homeData } from '@/pages/Home/Table/homeData';
// import { Switch } from '../../ui/Switch/Switch';
import project from '@/api/project';

const options = [
	{ id: 'standard', name: 'Standard', value: 0 },
	{ id: 'non-standard', name: 'Non-standard', value: 1 },
	{ id: 'arkiv', name: 'Arkiv', value: 2 },
];

const { getProducts } = project();

export default function ProjectRelation(props) {
	const { open, onClose, onSave, relations, groups, categories } = props;
	const projectStatus = useSelector((state) => state.content.projectStatusD);
	const productPrefix = useSelector((state) => state.content.productPrefix);

	const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
	const [filter, setFilter] = useState({
		search: '',
		// status: '',
		type: [],
		statuses: [],
		group: '',
		category: '',
		productprefixid: '',
	});
	const [search] = useDebounce(filter.search, 500);
	const [selectedRelations, setSelectRelations] = useState(relations);

	const oC = useRef(null);
	const selectGroupRef = useRef(null);
	const selectCategoryRef = useRef(null);
	const selectCategoryKode = useRef(null);

	const objFilters = useMemo(() => {
		const obj = { page_size: paginationModel.pageSize, order_by: '-productid' };
		if (search) {
			obj.search = search;
			// obj.max_size = true;
		}
		if (filter.group) {
			obj.group = filter.group;
		}
		if (filter.category) {
			obj.category = filter.category;
		}
		if (filter.productprefixid) {
			obj.productprefixid = filter.productprefixid;
		}
		if (filter.statuses.length > 0) {
			obj.status = filter.statuses.map((p) => p);
		}
		if (filter.type.length > 0) {
			obj.product_type = filter.type.map((p) => p);
		}

		return obj;
	}, [filter, search, paginationModel]);

	const {
		data: d,
		isFetching,
		isFetched,
	} = useQuery({
		queryKey: ['documents', search, filter, paginationModel],
		queryFn: () => {
			const obj = objFilters;
			if (paginationModel.page) obj.page = paginationModel.page + 1;
			if (paginationModel.page === 0 && 'page' in obj) delete obj.page;

			return getProducts(obj);
		},
		refetchOnWindowFocus: false,
	});

	const dataList = useMemo(() => d?.data?.results, [d]) || [];
	const dataCount =
		useMemo(() => {
			if (isFetched && d?.data?.count) {
				oC.current = d.data.count;
				return d.data.count;
			}
		}, [isFetched, d, oC]) ||
		oC.current ||
		0;

	// eslint-disable-next-line no-unused-vars
	const handleChangeFilter = (e, key) => {
		const val = e;
		setFilter((prevState) => {
			return { ...prevState, [key]: val };
		});
	};

	const removeSelectedRelation = (r) => {
		setSelectRelations((prevState) => {
			let newSelectRelations = [...prevState];
			newSelectRelations = newSelectRelations.filter((item) => item.productid !== r);
			return newSelectRelations;
		});
	};

	const columns = [
		{
			field: 'productprefix',
			headerName: 'Kode',
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'productcodenumber',
			headerName: 'Nummer',
			headerAlign: 'center',
			align: 'center',
		},
		{
			field: 'productname',
			headerName: 'Navn',
			headerAlign: 'center',
			align: 'left',
			flex: 1,
		},
		{
			field: 'productversion',
			headerName: 'Versjon',
			headerAlign: 'center',
			align: 'center',
		},
	];

	const usedCategories = useMemo(() => {
		if (filter.group) {
			return categories.filter((c) => c.group_id === filter.group)
		}
		return categories
	}, [categories, filter.group])
	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby='modal-project-relations'
			aria-describedby='modal-project-relations'
			className='w-full'
		>
			<Box sx={{ ...ModalStyle, top: '50svh' }} className='relative flex flex-col gap-4 w-[100%] 2xl:w-[90%] h-auto'>
				<span className='mb-4 text-2xl modal-title'>Lag relasjoner</span>
				<button className='absolute text-4xl top-4 right-4' onClick={onClose}>
					<Close fontSize='inherit' sx={{ color: 'var(--primary)' }} />
				</button>
				{isFetching && <Loader />}

				<div className='flex w-full gap-4'>
					<div className='flex flex-col w-full gap-4'>
						<div className='flex gap-4 items-center flex-wrap'>
							<TextField
								className='w-48 h-12 input-search'
								placeholder='Search'
								onChange={(e) => {
									setPaginationModel((prevState) => {
										return { ...prevState, page: 0 };
									});
									handleChangeFilter(e.target.value, 'search');
								}}
								size='small'
								InputProps={{
									startAdornment: (
										<InputAdornment position='start'>
											<Search style={{ color: 'black' }} />
										</InputAdornment>
									),
								}}
							/>

							<div className='flex items-center gap-1'>
								<span>Group</span>

								<Select
									ref={selectGroupRef}
									displayEmpty
									labelId='group'
									id='group'
									className='min-w-48 select-default '
									value={filter.group}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'group');
										if (val && val !== filter.group && filter.category) {
											handleChangeFilter('', 'category');
										}
										selectGroupRef.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg group--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg group--
										</em>
									</MenuItem>
									{groups?.map((p) => (
										<MenuItem key={p.id} value={p.id} name={p.groupname}>
											{p.groupname}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Category</span>

								<Select
									ref={selectCategoryRef}
									displayEmpty
									labelId='category'
									id='category'
									className='min-w-48 select-default '
									value={filter.category}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'category');
										selectCategoryRef.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg category--' />}
									placeholder='--Velg category--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg category--
										</em>
									</MenuItem>
									{usedCategories?.map((p) => (
										<MenuItem key={p.id} value={p.id} name={p.categoryname}>
											{p.categoryname}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Kode</span>

								<Select
									ref={selectCategoryKode}
									displayEmpty
									labelId='type'
									id='type'
									className='min-w-48 select-default '
									value={filter.productprefixid}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val, 'productprefixid');
										selectCategoryKode.current.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg type--'
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg kode--
										</em>
									</MenuItem>
									{productPrefix?.map((p) => (
										<MenuItem key={p.productprefixid} value={p.productprefixid} name={p.productprefix}>
											{p.productprefix}
										</MenuItem>
									))}
								</Select>
							</div>

							<div className='flex items-center gap-1'>
								<span>Status</span>

								<Select
									displayEmpty
									labelId='status'
									id='status'
									className='py-0 min-w-48 select-default'
									value={filter.statuses}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val.length && val.some((v) => v === '') ? [] : val, 'statuses');
										e.target.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg status--' className='py-0' />}
									placeholder='--Velg status--'
									multiple
									renderValue={(selected) =>
										selected.map((val) => {
											const option = projectStatus.find((opt) => opt.id_status === val);
											if (!option) return '';
											return <Chip key={option.id} label={option.label} className='me-1' />;
										})
									}
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300'>--Velg status--</em>
									</MenuItem>
									{projectStatus?.map((p) => (
										<MenuItem key={p.id} value={p.id_status} name={p.label}>
											{p.label}
										</MenuItem>
									))}
								</Select>
							</div>
							<div className='flex items-center gap-1'>
								<span>Type</span>

								<Select
									displayEmpty
									labelId='type'
									id='type'
									className='min-w-48 select-default '
									value={filter.type}
									onChange={(e) => {
										const val = e.target.value;
										handleChangeFilter(val.length && val.some((v) => v === '') ? [] : val, 'type');
										e.target.blur();
									}}
									input={<OutlinedInput label='' size='small' placeholder='--Velg type--' />}
									placeholder='--Velg type--'
									multiple
									renderValue={(selected) =>
										selected.map((val) => {
											const option = options.find((opt) => opt.value === val);
											if (!option) return '';
											return <Chip key={option.id} label={option.name} className='me-1' />;
										})
									}
									MenuProps={{
										disableAutoFocus: true,
									}}
								>
									<MenuItem value=''>
										<em className='text-gray-300' value=''>
											--Velg status--
										</em>
									</MenuItem>
									{options?.map((p) => (
										<MenuItem key={p.id} value={p.value} name={p.name}>
											{p.name}
										</MenuItem>
									))}
								</Select>
							</div>
						</div>
						<DataGrid
							autoHeight
							rows={dataList}
							loading={isFetching}
							columns={columns}
							rowsPerPageOptions={[10, 20, 50]}
							className='mx-auto lg:max-w-[1600px] w-full h-full mt-0'
							disableColumnFilter
							disableColumnSelector
							disableDensitySelector
							disableToolbarButton
							disableRowSelectionOnClick
							slots={{
								pagination: Pagination,
							}}
							getRowId={(row) => row.productid}
							getRowClassName={(params) => {
								let cellClass = params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd';
								if (selectedRelations.find((r) => r.productid === params.row.productid)) {
									cellClass += ' font-bold border border-black';
								}
								return cellClass;
							}}
							slotProps={{
								loadingOverlay: {
									variant: 'skeleton',
									noRowsVariant: 'skeleton',
								},
								pagination: {
									page: paginationModel.page,
									pageSize: paginationModel.pageSize,
									rowCount: dataCount,
								},
							}}
							onCellClick={(params, event) => {
								if (event.target.type === 'checkbox' || ['svg', 'path'].includes(event.target.tagName)) return;
								setSelectRelations((prevState) => {
									let newSelectRelations = [...prevState];
									const isExist = newSelectRelations.find((item) => item.productid === params.row.productid);

									if (isExist) {
										newSelectRelations = newSelectRelations.filter((item) => item.productid !== params.row.productid);
									} else {
										const d = params.row;
										d.amount = 1;
										d.code = 1;
										newSelectRelations.push(d);
									}
									return newSelectRelations;
								});
							}}
							initialState={{
								pagination: { paginationModel: { page: 0, pageSize: 10 } },
							}}
							rowCount={dataCount}
							paginationModel={paginationModel}
							paginationMode='server'
							onPaginationModelChange={(obj) => setPaginationModel(obj)}
						/>
					</div>
					<div className='flex flex-col items-center w-1/4 gap-4'>
						<span className='text-lg font-bold'>Relasjonsliste</span>
						<div className='flex flex-col gap-2'>
							{selectedRelations.length > 0 &&
								selectedRelations.map((r) => {
									const fName = `${r.productprefix} ${r.productcodenumber} ${r.component_productname || r.productname}`;
									return (
										<div
											title={fName}
											key={r.productid}
											className='flex items-start justify-center gap-1 px-2 py-1 border border-black rounded-md'
										>
											<span>{r.productprefix}</span>
											<span>{r.productcodenumber}</span>
											<button onClick={() => removeSelectedRelation(r.productid)}>
												<Close fontSize='medium' sx={{ color: 'var(--primary)' }} />
											</button>
										</div>
									);
								})}
						</div>
					</div>
				</div>
				<button
					className='w-auto ml-auto btn'
					onClick={() => {
						onSave(selectedRelations);
						onClose();
					}}
				>
					Lagre
				</button>
			</Box>
		</Modal>
	);
}
